import './QuizEditor.less';
import React, {useEffect, useState} from 'react';
import __ from "../../i18n";
import QuestionForm from "./question-form/QuestionForm";
import SectionForm from "./section-form/SectionForm";
import Tree from "./tree/Tree";
import Button from "../../../bootstrap/components/button/Button";
import {jsonCopy} from "../../../bootstrap/utils/json";
import CustomForm from "../../../bootstrap/components/custom-form/CustomForm";
import createEmptyQuiz from "./utils/createEmptyQuiz";
import Request from "../../../bootstrap/api/Request";
import Toasts, {TOAST_ERROR, TOAST_INFO, TOAST_SUCCESS, TOAST_WARN} from '../../../bootstrap/scomponents/toasts/Toasts';
import QuizSaveParcel from "../../api/endpoints/quiz/QuizSaveParcel";
import {genId as genPieceId} from "./utils/createEmptyPiece";
import QuizPlayer from "../../players/quiz/QuizPlayer";
import CheckboxInput from '../../../bootstrap/components/checkbox-input/CheckboxInput';


export default function QuizEditor({recordId = null, defaultData = {}, onDone, onCancel}) {

    const {
        quiz: defaultQuiz = createEmptyQuiz(),
        questionGroups: defaultQuestionGroups = [],
        questions: defaultQuestions = [],
    } = defaultData;

    const [quiz, setQuiz] = useState(defaultQuiz);

    const [sections, setSections] = useState(defaultQuestionGroups);
    const [questions, setQuestions] = useState(defaultQuestions.map(question => {
        try {
            return {
                ...question,
                answer_values: JSON.parse(question.answer_values)
            };
        } catch (e) {
            return {
                ...question,
                answer_values: [[]]
            };
        }
    }));
    const [pieces, setPieces] = useState(
        defaultQuestions.reduce((out, question) => {
            try {
                const _pieces = question.pieces ? JSON.parse(question.pieces) : [];
                _pieces.forEach(piece => {
                    piece.id = genPieceId();
                    piece.question_id = question.id;
                });
                return out.concat(_pieces);
            } catch (error) {
                console.error(error);
                return out;
            }
        }, [])
    );

    const [sectionErrors, setSectionErrors] = useState({});
    const [questionErrors, setQuestionErrors] = useState({});
    const [pieceErrors, setPieceErrors] = useState({});

    const filterErrors = (errors, newItems) => {
        const newIdSet = new Set(newItems.map(t => String(t.id)));
        const newErrors = {};
        for (let id in errors) {
            if (errors.hasOwnProperty(id) && newIdSet.has(id)) {
                newErrors[id] = errors[id];
            }
        }
        return newErrors;
    };

    useEffect(() => {
        setSectionErrors(errors => filterErrors(errors, sections));
    }, [sections]);

    useEffect(() => {
        setQuestionErrors(errors => filterErrors(errors, questions));
    }, [questions]);

    useEffect(() => {
        setPieceErrors(errors => filterErrors(errors, pieces));
    }, [pieces]);

    const [selectedNodeHash, setSelectedNodeHash] = useState('quiz');

    const getOutput = () => {
        const _quiz = {id: recordId, ...jsonCopy(quiz)};
        const _questionGroups = jsonCopy(sections);
        const _questions = jsonCopy(questions);
        _questions.forEach(question => {
            const _pieces = pieces.filter(piece => piece.question_id === question.id);
            _pieces.sort((a, b) => a.sort_order - b.sort_order);
            question.pieces = JSON.stringify(_pieces.map(piece => ({
                value: piece.value,
                text: null
            })));
            question.answer_values = JSON.stringify(question.answer_values);
        });

        return {
            quiz: _quiz,
            questionGroups: _questionGroups,
            questions: _questions,
        };
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSave = () => {
        const {quiz, questionGroups, questions} = getOutput();

        setIsSubmitting(true);

        Request.add(new QuizSaveParcel(quiz, questionGroups, questions))
            .then(data => {
                if (data.partialErrors) {
                    Toasts.add(TOAST_INFO, __('Some changes have been saved, some not.'));
                    Toasts.add(TOAST_ERROR, __(JSON.stringify(data.partialErrors)));
                } else {
                    Toasts.add(TOAST_SUCCESS, __('Your changes have been saved successfully'));
                }
                onDone();
            })
            .catch(error => {
                Toasts.add(TOAST_ERROR, error.getMessage());
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const [mode, setMode] = useState('edit');

    const isInvalid = (
        questions.length === 0
        || Object.keys(sectionErrors).length > 0
        || Object.keys(questionErrors).length > 0
        || Object.keys(pieceErrors).length > 0
    );

    return <div className="quiz-editor">
        <div className="top-bar">
            <Button
                title={__('Save Quiz')}
                appearance="solid-primary"
                size="small"
                icon="cloud-upload"
                onClick={handleSave}
                disabled={isInvalid || isSubmitting}
            />
            <CheckboxInput
                appearance="toggle"
                label={__('Preview mode')}
                onChange={checked => setMode(checked ? 'preview' : 'edit')}
                defaultValue={false}
                disabled={isInvalid}
            />
        </div>
        <div className="main">
            {
                mode === 'edit' &&
                <>
                    <div className="sidebar">
                        <Tree
                            selectedNodeHash={selectedNodeHash}
                            setSelectedNodeHash={setSelectedNodeHash}
                            setQuestions={setQuestions}
                            setSections={setSections}
                            setPieces={setPieces}
                            questions={questions}
                            sections={sections}
                            pieces={pieces}
                            questionErrors={questionErrors}
                            sectionErrors={sectionErrors}
                            pieceErrors={pieceErrors}
                        />
                    </div>
                    <div
                        key={selectedNodeHash}
                        className="workspace"
                    >
                        {
                            selectedNodeHash === 'quiz' &&
                            <CustomForm
                                fieldsConfig={{
                                    name: {
                                        name: __('Name'),
                                        type: 'text',
                                        rules: {
                                            required: true,
                                            maxLength: 191
                                        }
                                    }
                                }}
                                layoutConfig={['name']}
                                onInitialized={setQuiz}
                                onChange={setQuiz}
                                defaultValues={quiz}
                            />
                        }
                        {
                            selectedNodeHash.startsWith('section:') &&
                            <SectionForm
                                section={sections.find(section => {
                                    return section.id === Number(selectedNodeHash.split(':')[1]);
                                })}
                                setSections={setSections}
                                setSectionErrors={setSectionErrors}
                                setSelectedNodeHash={setSelectedNodeHash}
                                questions={questions}
                                setQuestions={setQuestions}
                                setPieces={setPieces}
                            />
                        }
                        {
                            selectedNodeHash.startsWith('question:') &&
                            <QuestionForm
                                question={questions.find(question => {
                                    return question.id === Number(selectedNodeHash.split(':')[1]);
                                })}
                                setQuestions={setQuestions}
                                setQuestionErrors={setQuestionErrors}
                                pieces={pieces}
                                setPieces={setPieces}
                                setPieceErrors={setPieceErrors}
                                setSelectedNodeHash={setSelectedNodeHash}
                            />
                        }
                    </div>
                </>
            }
            {
                mode === 'preview' &&
                <QuizPlayer
                    {...getOutput()}
                />
            }
        </div>
    </div>;
}
